import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Play, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '@/client/lib/api';
import type { IntroTokenResponse } from '@/client/types/courses';

type Props = {
  slug: string;
  /** Used as the "click to play" thumbnail; falls back to a gradient. */
  thumbnailUrl: string | null;
  title: string;
};

/**
 * Public intro-video player. Lazy-loads the signed playback token only when
 * the user clicks Play, so we don't burn an API call on every page view. No
 * watermark — this is marketing content open to non-enrolled visitors.
 *
 * Outer rounded/glass frame is supplied by the call site (e.g. CourseDetail).
 */
export function IntroVideoPlayer({ slug, thumbnailUrl, title }: Props) {
  const { t } = useTranslation();
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  const tokenMutation = useMutation({
    mutationFn: () =>
      api<IntroTokenResponse>(`/api/v1/courses/${slug}/intro-token`, { method: 'POST' }),
    onSuccess: (data) => setIframeUrl(data.iframeUrl),
  });

  if (iframeUrl) {
    return (
      <div className="relative w-full aspect-video bg-black">
        <iframe
          src={iframeUrl}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          title={title}
        />
        <button
          type="button"
          onClick={() => setIframeUrl(null)}
          className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/55 backdrop-blur-md border border-white/15 text-white/90 hover:bg-black/75 hover:text-white hover:-translate-y-px transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          aria-label={t('courses.intro_video_close')}
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => tokenMutation.mutate()}
      disabled={tokenMutation.isPending}
      className="group relative block w-full aspect-video overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      aria-label={t('courses.intro_video_label')}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary-light/55 to-primary/30" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-primary-900/55 via-primary-900/15 to-transparent transition-opacity duration-300 group-hover:opacity-90" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="relative inline-flex items-center gap-3 rounded-pill bg-white/92 backdrop-blur-md px-7 py-3.5 font-display text-body-large text-primary-dark shadow-[0_8px_28px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.6)] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_12px_36px_rgba(117,59,189,0.30),inset_0_1px_0_rgba(255,255,255,0.7)]">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-dark text-white shadow-[0_2px_8px_rgba(117,59,189,0.4)]">
            <Play size={16} fill="currentColor" strokeWidth={0} className="translate-x-px" />
          </span>
          {tokenMutation.isPending ? t('player.loading') : t('courses.intro_video_label')}
        </span>
      </div>
    </button>
  );
}
